import React, { useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { t } from 'i18next'
import { Modal } from 'react-bootstrap'
import {
  TextField,
  Button,
  Typography,
  Box,
  IconButton,
  CircularProgress,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import PersonOutlineIcon from '@mui/icons-material/PersonOutline'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import { fetchSearchCustomers } from 'redux/actions/clientsActions'
import './duplicateRucModal.css'
import { customerTypeId } from 'Utils/constants'

export default function DuplicateRucModal({
  open,
  onClose,
  duplicateClients = [],
  onContinue,
  isLoading = false,
  editingCustomerId = null,
  initialOperationalNumber = '',
  formRuc = '', // RUC del formulario para validar duplicados
}) {
  const dispatch = useDispatch()
  const companyId = useSelector(state => state.company.CompanyId)
  const [operationalNumber, setOperationalNumber] = useState('')
  const [requiredError, setRequiredError] = useState('')
  const [apiError, setApiError] = useState('')
  const [isChecking, setIsChecking] = useState(false)
  const [isValid, setIsValid] = useState(false) // ✅ éxito de la "API"

  const inputRef = useRef(null)
  const debounceRef = useRef(null)

  const handleClose = () => {
    setOperationalNumber('')
    setRequiredError('')
    setApiError('')
    setIsChecking(false)
    setIsValid(false)
    onClose?.()
  }

  const handleContinue = () => {
    setRequiredError('')

    if (!operationalNumber.trim()) {
      setRequiredError(t('CREATE_CLIENT_T.OPERATIONAL_NOTICE_NUMBER_REQUIRED'))
      return
    }

    if (apiError || isChecking) return

    onContinue?.({
      operationalNumber: operationalNumber.trim(),
      duplicateClients,
    })
  }

  // Inicializar valor cuando el modal se abre
  useEffect(() => {
    if (open) {
      setOperationalNumber(initialOperationalNumber || '')
      if (!initialOperationalNumber) {
        setRequiredError(
          t('CREATE_CLIENT_T.OPERATIONAL_NOTICE_NUMBER_REQUIRED')
        )
      }
    }
  }, [open, initialOperationalNumber])

  // Foco inicial
  useEffect(() => {
    if (open) {
      requestAnimationFrame(() => inputRef.current?.focus())
    }
  }, [open])

  // Debounce + validación de API para número de aviso operacional
  useEffect(() => {
    if (!open) return

    const value = operationalNumber.trim()

    // limpiar timer anterior
    if (debounceRef.current) clearTimeout(debounceRef.current)

    // cada vez que cambia el valor, limpiamos estados de validación
    setApiError('')
    setIsValid(false)
    setIsChecking(false)

    // si hay menos de 5 caracteres, no se consulta nada
    if (value.length < 5) return

    setIsChecking(true)

    // Implementar debounce de 500ms
    debounceRef.current = setTimeout(async () => {
      if (!open) return

      try {
        // Buscar clientes que coincidan con el número de aviso operacional
        const payload = {
          companyId,
          GlobalExecution: true,
          SearchText: value,
          RoleName: 'Customer',
          PageSize: 10,
          pageNumber: 1,
          CustomerTypeId: customerTypeId.legal,
        }

        const customers = await dispatch(fetchSearchCustomers(payload))

        setIsChecking(false)

        if (customers && customers.length > 0) {
          // Filtrar clientes:
          // 1. Excluir el cliente que se está editando
          // 2. Solo considerar clientes cuyo RUC coincida con el del formulario
          const filteredCustomers = customers.filter(customer => {
            // Excluir el cliente que se está editando
            if (
              editingCustomerId &&
              customer.CustomerId === editingCustomerId
            ) {
              return false
            }
            // Solo considerar duplicados si el RUC coincide con el del formulario
            if (formRuc) {
              return (
                customer.NumberId?.trim()?.toUpperCase() ===
                formRuc.trim().toUpperCase()
              )
            }
            return true
          })

          if (filteredCustomers.length > 0) {
            // ❌ Número de aviso operacional duplicado
            setIsValid(false)
            setApiError(
              t('CREATE_CLIENT_T.DUPLICATE_DOC_MODAL_DUPLICATE_OPERATIONAL')
            )
          } else {
            // ✅ Número de aviso operacional válido
            setIsValid(true)
            setApiError('')
          }
        } else {
          // ✅ Número de aviso operacional válido (no hay coincidencias)
          setIsValid(true)
          setApiError('')
        }
      } catch (error) {
        console.error('Error al validar número de aviso operacional:', error)
        setIsChecking(false)
        setApiError(t('CREATE_CLIENT_T.DUPLICATE_DOC_MODAL_VALIDATION_ERROR'))
      }
    }, 500) // debounce

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current)
    }
  }, [operationalNumber, open, dispatch, companyId, editingCustomerId, formRuc])

  const hasError = Boolean(requiredError || apiError)

  const endAdornment = (() => {
    if (isChecking) {
      return <CircularProgress size={18} />
    }
    if (apiError) {
      return <ErrorOutlineIcon sx={{ color: '#dc2626' }} />
    }
    if (isValid) {
      return <CheckCircleOutlineIcon sx={{ color: '#16a34a' }} />
    }
    return null
  })()

  return (
    <Modal
      show={open}
      centered
      onHide={handleClose}
      backdrop="static"
      className="duplicate-ruc-backdrop"
      dialogClassName="duplicate-ruc-dialog">
      {/* HEADER */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 3,
          pt: 2.8,
          pb: 1.8,
        }}>
        <Typography sx={{ fontSize: 22, fontWeight: 600 }}>
          {t('CREATE_CLIENT_T.DUPLICATE_DOC_MODAL_TITLE')}
        </Typography>

        <IconButton onClick={handleClose}>
          <CloseIcon sx={{ color: '#1d4ed8' }} />
        </IconButton>
      </Box>

      {/* BODY */}
      <Box sx={{ px: 3, pb: 2 }}>
        <Typography sx={{ fontSize: 14, mb: 2 }}>
          {t('CREATE_CLIENT_T.DUPLICATE_DOC_MODAL_DESCRIPTION')}
        </Typography>

        {/* Lista de clientes */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 1.4,
            maxHeight: 220,
            overflowY: 'auto',
            pr: 0.5,
            mb: 2.5,
          }}>
          {duplicateClients.map((client, i) => (
            <Box
              key={i}
              sx={{
                background: '#eef4ff',
                borderRadius: '7px',
                px: 2,
                py: 1.4,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: 1.5,
              }}>
              {/* Ícono + nombre (ocupa el espacio disponible y hace wrap) */}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.2,
                  flex: 1,
                  minWidth: 0,
                }}>
                <PersonOutlineIcon
                  sx={{ color: 'var(--secondary-color)', flexShrink: 0 }}
                />
                <Typography
                  sx={{
                    color: 'var(--secondary-color)',
                    fontWeight: 400,
                    fontSize: 14.5,
                    wordBreak: 'break-word',
                  }}>
                  {client?.FullName}
                </Typography>
              </Box>

              {/* Aviso operacional (columna fija, solo si existe) */}
              {client?.OperationNoticeNumber && (
                <Typography
                  sx={{
                    color: 'var(--secondary-color)',
                    fontWeight: 600,
                    fontSize: 14.5,
                    flexShrink: 0,
                    whiteSpace: 'nowrap',
                  }}>
                  {client?.OperationNoticeNumber}
                </Typography>
              )}

              {/* RUC fijo a la derecha */}
              <Typography
                sx={{
                  color: 'var(--secondary-color)',
                  fontWeight: 400,
                  fontSize: 14.5,
                  flexShrink: 0,
                  whiteSpace: 'nowrap',
                }}>
                {t('CREATE_CLIENT_T.DUPLICATE_DOC_MODAL_DOC_PREFIX')}{' '}
                {client?.NumberId}
              </Typography>
            </Box>
          ))}
        </Box>

        <Typography sx={{ fontSize: 14, mb: 1 }}>
          {t('CREATE_CLIENT_T.DUPLICATE_DOC_MODAL_OPERATIONAL_HINT')}
        </Typography>

        <Typography sx={{ fontSize: 14, mb: 0.5 }}>
          {t('CREATE_CLIENT_T.DUPLICATE_DOC_MODAL_OPERATIONAL_LABEL')}
        </Typography>

        <TextField
          fullWidth
          inputRef={inputRef}
          value={operationalNumber}
          onChange={e => {
            setOperationalNumber(e.target.value)
            if (e.target.value.trim()) {
              setRequiredError('')
            } else {
              setRequiredError(
                t('CREATE_CLIENT_T.OPERATIONAL_NOTICE_NUMBER_REQUIRED')
              )
            }
          }}
          error={hasError}
          helperText={requiredError || apiError || ' '}
          disabled={isLoading}
          autoComplete="off"
          InputProps={{
            endAdornment,
            inputProps: {
              style: {
                fontSize: 15,
              },
            },
          }}
          FormHelperTextProps={{
            sx: {
              fontSize: 13.5,
              mt: 0.4,
            },
          }}
          sx={{
            '& .MuiOutlinedInput-root': { borderRadius: '8px' },
          }}
        />
      </Box>

      {/* FOOTER */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          px: 3,
          pb: 2.8,
          pt: 0.5,
        }}>
        <Button
          variant="outlined"
          sx={{
            textTransform: 'none',
            fontSize: 14,
            minWidth: 130,
            height: 44,
            borderRadius: '5px',
            color: '#003082',
            borderColor: '#003082',
            '&:hover': {
              borderColor: '#003082',
              backgroundColor: 'rgba(0, 48, 130, 0.04)',
            },
            '&:disabled': {
              color: '#c4c4c4',
              borderColor: '#c4c4c4',
            },
          }}
          onClick={handleClose}
          disabled={isLoading}>
          {t('CREATE_CLIENT_T.DUPLICATE_DOC_MODAL_CANCEL')}
        </Button>

        <Button
          variant="contained"
          onClick={handleContinue}
          disabled={
            !operationalNumber.trim() ||
            isLoading ||
            isChecking ||
            !!apiError ||
            !isValid
          }
          sx={{
            textTransform: 'none',
            fontSize: 14,
            minWidth: 160,
            height: 44,
            borderRadius: '5px',
            backgroundColor: '#003082',
            color: '#fff',
            boxShadow: 'none',
            '&:hover': {
              backgroundColor: '#003082',
              boxShadow: 'none',
            },
            '&:disabled': {
              backgroundColor: '#c4c4c4',
              color: '#fff',
            },
          }}>
          {isLoading ? (
            <>
              <CircularProgress size={16} sx={{ mr: 1, color: '#fff' }} />
              {t('CREATE_CLIENT_T.DUPLICATE_DOC_MODAL_PROCESSING')}
            </>
          ) : (
            t('CREATE_CLIENT_T.DUPLICATE_DOC_MODAL_CONTINUE')
          )}
        </Button>
      </Box>
    </Modal>
  )
}
